/**
 * DLE 評価の期間キー ("YYYY/MM" / "YYMQn") のラベルと会計年度ユーティリティ。
 *
 * デザイン評価側 (`idt-kpi/periods.ts`, `designEvaluation.ts`) に同じ役割の関数があるが、
 * **月キーの区切り文字が違う**ので流用しない。デザイン評価は `"YYYY-MM"`、DLE 評価は
 * 集計シートの `対象月` 列と同じ `"YYYY/MM"` を API の period にしている。シートの値と
 * 画面の期間表記を一致させておくと、点が合わないときに運用者がシートの行を直接引ける。
 *
 * 会計年度は 7 月始まり (デザイン評価・`四半期分析` タブと同じ)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価の月キー表示ラベルと 7 月始まり会計年度の月一覧
 * @graph-connects none
 */

/** "YYYY/MM" を分解する。形式外は null。 @graph-connects none */
function parseDleMonth(month: string): { year: number; month: number } | null {
  const matched = /^(\d{4})\/(\d{2})$/.exec(month);
  if (!matched) return null;
  return { year: parseInt(matched[1], 10), month: parseInt(matched[2], 10) };
}

/** "YYYY/MM" → "2026年8月"。形式外はそのまま返す。 @graph-connects none */
export function dleMonthLabel(month: string): string {
  const parsed = parseDleMonth(month);
  if (!parsed) return month;
  return `${String(parsed.year)}年${String(parsed.month)}月`;
}

/** "YYYY/MM" → "26/8" (推移折線の x 軸ラベル)。形式外はそのまま返す。 @graph-connects none */
export function dleMonthShortLabel(month: string): string {
  const parsed = parseDleMonth(month);
  if (!parsed) return month;
  return `${String(parsed.year).slice(2)}/${String(parsed.month)}`;
}

/**
 * "YYYY/MM" の属する会計年度の開始年 (7 月始まり)。
 * 例: 2026/06 → 2025 / 2026/07 → 2026。形式外は 0。
 * @graph-connects none
 */
export function dleFyStartYearOf(month: string): number {
  const parsed = parseDleMonth(month);
  if (!parsed) return 0;
  return parsed.month >= 7 ? parsed.year : parsed.year - 1;
}

/**
 * 会計年度開始年 → その 12 ヶ月 ("YYYY/MM", 7 月始まり〜翌 6 月) を昇順で返す。
 * 推移折線の x 軸を「データのある月だけ」ではなく完全な 12 ヶ月にして、評価が無い月が
 * 欠けていることを目で見えるようにする。
 * @graph-connects none
 */
export function dleFyMonths(startYear: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < 12; i++) {
    const raw = 7 + i; // 7..18
    const year = raw <= 12 ? startYear : startYear + 1;
    const month = raw <= 12 ? raw : raw - 12;
    out.push(`${String(year)}/${String(month).padStart(2, '0')}`);
  }
  return out;
}

/**
 * 会計年度の 12 ヶ月を、データが存在する範囲 [first, last] に切り詰める。
 *
 * **切り詰めないと推移が読めなくなる。** 会計年度は 7 月始まりなので、年度が明けた直後は
 * 12 スロット中 1〜2 ヶ月しか埋まらず、折線が端に潰れた「壊れたグラフ」に見える
 * (旧 Form の集計が数ヶ月遅れることもあり、当年度が薄いのは常態)。月キーは "YYYY/MM" で
 * ゼロ埋めされているため、辞書順の比較がそのまま時系列の比較になる。
 *
 * `first` / `last` が無い (データ 0 件) ときは切り詰めない。
 * @graph-connects none
 */
export function clipDleMonthsToData(
  months: readonly string[],
  first: string | null,
  last: string | null,
): string[] {
  return months.filter(
    (month) => (first == null || month >= first) && (last == null || month <= last),
  );
}

/** 会計年度開始年 → 表示ラベル (例: "2025/7〜2026/6")。 @graph-connects none */
export function dleFyLabel(startYear: number): string {
  return `${String(startYear)}/7〜${String(startYear + 1)}/6`;
}
